import {
  Button,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { useEffect, useMemo, useState } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import {
  useLazyGetStoreListQuery,
  useDeleteStoreMutation,
  useUpdateStoreMutation,
} from "../../services/store";
import { useBulkUploadMutation } from "../../services/bulkupload";
import { UploadMediaWithoutCompress } from "../../utils/uploadMedia";
import CloseIcon from "@mui/icons-material/Close";
import { showError, showToast } from "../../helpers/showToast";
import DeleteConfirmationModal from "../../components/DeleteModal";
import Pagination from "../../components/Panigation";
import { isValidInput } from "../../utils/validations";
import usePermission from "../../hooks/userAccess";

const ManageStores = () => {
  const navigate = useNavigate();
  const { isAdd, isEdit, isDelete, isView } = usePermission({
    label: "Store Management",
    page_permission: "isView",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [Status, setStatus] = React.useState("2");

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [triggerList, { data: listRes, isFetching }] =
    useLazyGetStoreListQuery();
  const [deleteStore, { isLoading: isDeleting }] = useDeleteStoreMutation();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();
  const [bulkUpload, { isLoading: isBulkUploading }] = useBulkUploadMutation();

  const [openBulk, setOpenBulk] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const toggleBulk = () => setOpenBulk(!openBulk);

  useEffect(() => {
    triggerList({ page, limit, search: debouncedSearchTerm, status: Status });
  }, [triggerList, page, limit, debouncedSearchTerm, Status]);

  type RowType = {
    id: string;
    name: string;
    code: string;
    status: string;
    address: string;
    region: string;
    date: string;
    raw: any;
  };

  const rows: RowType[] = useMemo(() => {
    const data = (listRes as any)?.data;
    const list = Array.isArray(data?.store) ? data?.store : data?.list || [];
    setTotalCount((data?.count as number) || (data?.totalCount as number) || 0);
    return list.map((item: any) => ({
      id: item?._id || item?.id,
      name: item?.name,
      code: item?.storeCode,
      status: item?.status === 1 ? "Open" : "Closed",
      address: item?.address,
      region: item?.city,
      date: item?.createdAt
        ? new Date(item.createdAt).toLocaleDateString()
        : "-",
      raw: item,
    }));
  }, [listRes]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const toggleDeleteModal = () => setOpenDelete(!openDelete);

  const handleDelete = async () => {
    try {
      await deleteStore({ id: activeId }).unwrap();
      toggleDeleteModal();
      showToast("Store deleted successfully");
      triggerList({ page, limit, search: debouncedSearchTerm, status: Status });
    } catch (e) {
      console.log(e);
    }
  };

  const handleToggle = async (row: RowType, nextChecked: boolean) => {
    const id = row?.id;
    if (!id) return;
    try {
      await updateStore({ body: { isActive: nextChecked }, id }).unwrap();
      triggerList({ page, limit, search: debouncedSearchTerm, status: Status });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Store Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard", { replace: true })}>
            Dashboard
          </button>{" "}
          - Store Management
        </p>
      </div>
      {isView && (
        <div className="cards">
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search..."
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
              <div className=" form">
                <Select fullWidth value={Status} onChange={handleStatusChange}>
                  <MenuItem disabled value="default">
                    Select Status
                  </MenuItem>
                  <MenuItem value="2">All</MenuItem>
                  <MenuItem value="1">Open</MenuItem>
                  <MenuItem value="0">Closed</MenuItem>
                </Select>
              </div>
            </div>
            <div className="right_s">
              <Button className="btn btn_black" onClick={toggleBulk}>
                <FileUploadIcon /> Bulk Upload
              </Button>
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-stores/add")}
              >
                <AddIcon /> Add Store
              </Button>
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Store Code</TableCell>
                  <TableCell>Store Name</TableCell>
                  <TableCell>Address / Region</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Disable/Enable</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: RowType) => (
                    <TableRow hover key={row?.id}>
                      <TableCell>
                        <span
                          onClick={() =>
                            navigate(`/manage-stores/detail/${row?.id}`)
                          }
                        >
                          <b>{row?.code || ""}</b>
                        </span>
                      </TableCell>
                      <TableCell>{row?.name || ""}</TableCell>
                      <TableCell>
                        {row?.address || ""}, {row?.region || ""}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`status_btn dark ${row.status === "Open"
                              ? "c_success"
                              : row.status === "Closed"
                                ? "c_danger"
                                : ""
                            }`}
                        >
                          {row.status}
                        </span>
                      </TableCell>
                      <TableCell>{row?.date}</TableCell>
                      <TableCell>
                        <SwitchToggle
                          checked={Boolean(
                            row?.raw?.isActive ?? row?.raw?.status === 1
                          )}
                          onChange={(checked) => handleToggle(row, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          {isView && (
                            <IconButton
                              onClick={() =>
                                navigate(`/manage-stores/detail/${row?.id}`)
                              }
                            >
                              <img src="/static/images/eye_icon.svg" alt="" />
                            </IconButton>
                          )}
                          {isEdit && (
                            <IconButton
                              onClick={() =>
                                navigate(`/manage-stores/edit/${row?.id}`)
                              }
                            >
                              <img src="/static/images/edit2_icon.svg" alt="" />
                            </IconButton>
                          )}
                          {isDelete && (
                            <IconButton
                              onClick={() => {
                                setActiveId(row?.id);
                                toggleDeleteModal();
                              }}
                            >
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt=""
                              />
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            limit={limit}
            setLimit={setLimit}
            module={rows || []}
            page={page}
            setPage={setPage}
            onPageChange={onPageChange}
            totalPages={Math.max(
              1,
              Math.ceil((totalCount || 0) / (limit || 10))
            )}
          />
        </div>
      )}
      {/* Bulk Upload Modal */}
      <Modal
        className="modal deleteConfirmation_modal"
        open={openBulk}
        onClose={toggleBulk}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>Bulk Upload Stores</h2>
              <button type="button" className="btn-close" onClick={toggleBulk}>
                <CloseIcon />
              </button>
            </div>

            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files && files[0]) setSelectedFile(files[0]);
              }}
              style={{
                border: "2px dashed #d0d5dd",
                borderRadius: 12,
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                background: "#fff",
                marginBottom: 16,
              }}
            >
              <div style={{ textAlign: "center", color: "#667085" }}>
                <div
                  style={{ fontSize: 18, color: "#101828", marginBottom: 4 }}
                >
                  Drop anywhere to import
                </div>
                <div>
                  Or select{" "}
                  <label style={{ color: "#2e90fa", cursor: "pointer" }}>
                    files
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e: any) => {
                        const f = e.target.files?.[0];
                        if (f) setSelectedFile(f);
                      }}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
                {selectedFile && (
                  <div style={{ marginTop: 12, color: "#344054" }}>
                    Selected: <b>{selectedFile.name}</b>
                  </div>
                )}
              </div>
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                type="button"
                onClick={toggleBulk}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                type="button"
                disabled={!selectedFile || isBulkUploading}
                onClick={async () => {
                  if (!selectedFile) return;
                  try {
                    const uploadRes: any = await UploadMediaWithoutCompress(
                      selectedFile
                    );
                    const fileUrl =
                      uploadRes?.data?.image ||
                      uploadRes?.data?.images?.[0]?.url ||
                      "";
                    if (!fileUrl) {
                      showError("Failed to upload file. Please try again.");
                      return;
                    }
                    await bulkUpload({ fileUrl } as any).unwrap();
                    showToast("Bulk upload started successfully");
                    setSelectedFile(null);
                    toggleBulk();
                    triggerList({
                      page,
                      limit,
                      search: debouncedSearchTerm,
                      status: Status,
                    });
                  } catch (e: any) {
                    showError(
                      e?.data?.message || e?.message || "Upload failed"
                    );
                  }
                }}
              >
                {isBulkUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this store?"
      />
    </>
  );
};

export default ManageStores;
